"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { User } from "@/common/model/User";
import UserManager from "@/components/user/UserManager";
import axiosInstance from "@/api/axiosInstance";
import { ENDPOINTS } from "@/constants/api";

const UsersPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [activeTab, setActiveTab] = useState<string>("user-details");
    const [isLoadingUser, setIsLoadingUser] = useState(false);

    // Use refs to avoid dependency loops
    const selectedUserRef = useRef(selectedUser);
    selectedUserRef.current = selectedUser;

    // Stable helper to update URL params — reads current search inside callback
    const updateUrlParams = useCallback((params: { [key: string]: string | null }) => {
        const searchParams = new URLSearchParams(window.location.search);
        Object.entries(params).forEach(([key, value]) => {
            if (value === null || value === undefined || value === "") {
                searchParams.delete(key);
            } else {
                searchParams.set(key, value);
            }
        });
        const newSearch = searchParams.toString();
        navigate({ pathname: location.pathname, search: newSearch }, { replace: true });
    }, [location.pathname, navigate]);

    // Load user by ID from URL
    const loadUserById = useCallback(async (userId: number) => {
        setIsLoadingUser(true);
        try {
            const response = await axiosInstance.get(`${ENDPOINTS.ADMIN.USERS}/${userId}`);
            if (response.data) {
                setSelectedUser({
                    id: response.data.userId,
                    username: response.data.username || response.data.name,
                    name: response.data.name || "",
                    accesstoken: "",
                    gender: response.data.gender || "",
                    isadmin: false,
                    maxairtime: response.data.maxairtime || 0,
                    maxdistance: response.data.maxdistance || 0,
                    maxheight: response.data.maxheight || 0,
                    ownprofile: false,
                    privateaccount: response.data.privateAccount || false,
                });
            }
        } catch (error: any) {
            console.error("Failed to load user by ID:", error);
            // Don't clear URL params on 401 — the axios interceptor handles redirect
            if (error.response?.status !== 401) {
                updateUrlParams({ userId: null, tab: null });
            }
        } finally {
            setIsLoadingUser(false);
        }
    }, [updateUrlParams]);

    // Read URL params on mount and when URL search changes
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const userIdFromUrl = searchParams.get("userId");
        const tabFromUrl = searchParams.get("tab");
        const sessionIdFromUrl = searchParams.get("sessionId");
        const uploadIdFromUrl = searchParams.get("uploadId");

        // Handle tab from URL (redirect legacy "support" tab to "device-details")
        if (tabFromUrl) {
            setActiveTab(tabFromUrl === "support" ? "device-details" : tabFromUrl);
        }

        // Handle user from URL (only if different from current)
        if (userIdFromUrl) {
            const parsedUserId = parseInt(userIdFromUrl, 10);
            const currentUser = selectedUserRef.current;
            if (!isNaN(parsedUserId) && (!currentUser || currentUser.id !== parsedUserId)) {
                loadUserById(parsedUserId);
            }
        }

        // Store session/upload IDs for child components
        if (sessionIdFromUrl) {
            sessionStorage.setItem("deeplink-sessionId", sessionIdFromUrl);
        } else {
            sessionStorage.removeItem("deeplink-sessionId");
        }

        if (uploadIdFromUrl) {
            sessionStorage.setItem("deeplink-uploadId", uploadIdFromUrl);
        } else {
            sessionStorage.removeItem("deeplink-uploadId");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    // Handle user selection
    const handleSelectUser = useCallback((user: User | null) => {
        setSelectedUser(user);
        if (user) {
            updateUrlParams({
                userId: String(user.id),
                tab: null,
                sessionId: null,
                uploadId: null
            });
            setActiveTab("user-details");
        } else {
            updateUrlParams({
                userId: null,
                tab: null,
                sessionId: null,
                uploadId: null
            });
            setActiveTab("user-details");
        }
    }, [updateUrlParams]);

    // Handle tab change
    const handleTabChange = useCallback((tabId: string) => {
        setActiveTab(tabId);
        updateUrlParams({
            tab: tabId,
            sessionId: null,
            uploadId: null
        });
    }, [updateUrlParams]);

    // Handle session selection (from SessionsTab)
    const handleSessionSelect = useCallback((sessionId: number | null) => {
        if (sessionId) {
            updateUrlParams({ sessionId: String(sessionId) });
        } else {
            updateUrlParams({ sessionId: null });
        }
    }, [updateUrlParams]);

    // Handle upload selection (from DeviceDetailsTab > Support Uploads)
    const handleUploadSelect = useCallback((uploadId: string | null) => {
        if (uploadId) {
            updateUrlParams({ uploadId });
        } else {
            updateUrlParams({ uploadId: null });
        }
    }, [updateUrlParams]);

    // Show loading state while fetching user from URL
    if (isLoadingUser) {
        return (
            <div className="h-full flex items-center justify-center">
                <div className="flex items-center gap-3">
                    <div className="relative">
                        <div className="w-8 h-8 rounded-full border-3 border-gray-200"></div>
                        <div className="absolute top-0 left-0 w-8 h-8 rounded-full border-3 border-cyan-500 border-t-transparent animate-spin"></div>
                    </div>
                    <p className="text-sm text-gray-600">Loading user...</p>
                </div>
            </div>
        );
    }

    return (
        <UserManager
            selectedUser={selectedUser}
            onSelectUser={handleSelectUser}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            onSessionSelect={handleSessionSelect}
            onUploadSelect={handleUploadSelect}
        />
    );
};

export default UsersPage;
